import { useCallback, useEffect, useMemo, useState } from 'react';
import { Route, useRouter } from '@bymason-private/route-provider';
import { useAuth } from '@bymason-private/auth-provider';
import {
  Box,
  ControllerAlert as Alert,
  Typography,
  Divider,
} from '@bymason-private/legacy-pattern-components';
import {
  useToast,
  LegacyDetailsList,
  DetailItem,
  DeploymentsList,
  GroupingToggleButton,
  GridGroupingOption,
  retreiveGridGrouping,
  persistGridGrouping,
  DEFAULT_GROUP_NAME,
  DEPLOY_TYPE_MAP,
  DeleteGroupModal,
  DeployToGroupModal,
} from '../..';
import {
  ArtifactDeployment,
  ArtifactType,
  GroupModel,
  useGetGroups,
  useGetGroupDeployments,
  useGetGroupDevices,
} from '@bymason/controller-api-hooks';
import { compareSemvers, formatLongDateTimeString, pluralize } from '@bymason/utils';

export { ArtifactType, ArtifactDeployment } from '@bymason/controller-api-hooks';

interface GroupDetailsViewProps {
  name?: string;
  id?: string;
  groupsViewRoute: Route;
  projectViewRoute?: Route;
  showDeleteGroupModal: boolean;
  controllerBuildEnabled?: boolean;
  onCloseDeleteGroupModal: () => void;
  onDeleteGroupSuccess?: (group: string) => void;
  onDeleteGroupError?: (message: string, code?: number) => void;
  showDeployModal: ArtifactType | null;
  onCloseDeployModal: () => void;
  onDeploySuccess?: (deploy: ArtifactDeployment) => void;
  onDeployError?: (message: string, code?: number) => void;
  onCreateNewProject?: () => void;
  onGroupDetailsLoaded?: (groupDetails: GroupModel, deviceCount?: number) => void;
}

export function GroupDetailsView({
  name = '',
  id = '',
  groupsViewRoute,
  projectViewRoute,
  showDeleteGroupModal,
  controllerBuildEnabled,
  onCloseDeleteGroupModal,
  onDeleteGroupSuccess,
  onDeleteGroupError,
  showDeployModal,
  onCloseDeployModal,
  onDeploySuccess,
  onCreateNewProject,
  onGroupDetailsLoaded,
}: GroupDetailsViewProps) {
  const { authToken = '' } = useAuth();
  const { navigate } = useRouter();
  const [setToastMessage, setToastSeverity] = useToast();
  const [deploymentsView, setDeploymentsView] = useState<GridGroupingOption>(
    retreiveGridGrouping('deploys') || 'grouped'
  );
  const [getGroups, groups, groupDetailsError, , groupDetailsStatus] = useGetGroups(authToken);
  const [getDeployments, deployments, deploymentsError, deploymentsBusy] = useGetGroupDeployments(authToken);
  const [getDevices, devices] = useGetGroupDevices(authToken);

  const groupDetails = useMemo(() => groups?.data[0], [groups]);

  const handleChangeDeploymentsView = (event: React.MouseEvent<HTMLElement, MouseEvent>, value: unknown) => {
    if (value) {
      const newValue = value as GridGroupingOption;
      persistGridGrouping('deploys', newValue);
      setDeploymentsView(newValue);
    }
  };

  const deviceCount = useMemo(() => (devices ? devices.data?.length || 0 : undefined), [devices]);

  const detailItems = useMemo<DetailItem[]>(() => {
    const desc = groupDetails?.description ?? '';
    const createdAt = groupDetails?.createdAt ?? '';
    const deviceType = groupDetails?.deviceFamily ?? '';
    const apiLevel = groupDetails?.apiLevel ?? '';

    return [
      {
        type: 'description',
        label: 'Description',
        value: <Typography style={{ overflowWrap: 'break-word' }}>{desc}</Typography>,
      },
      { type: 'createdAt', label: 'Created', value: createdAt ? formatLongDateTimeString(createdAt) : '' },
      {
        type: 'devices',
        label: 'Device',
        value: deviceType
          ? `${deviceType} (${pluralize(deviceCount || 0)`$count ${['device', 'devices']}`})`
          : 'No devices',
      },
      { type: 'api', label: 'API', value: apiLevel || 'None' },
    ];
  }, [groupDetails, deviceCount]);

  const minDeployedOsVersion = useMemo(() => {
    return deployments
      ? deployments
          .filter((dep) => dep.type === ArtifactType.Ota && dep.version)
          .sort((depA, depB) => compareSemvers(depA.version, depB.version))[0]?.version
      : undefined;
  }, [deployments]);

  const isDefaultGroup = useMemo(
    () => groupDetails && groupDetails.name.toLowerCase() === DEFAULT_GROUP_NAME,
    [groupDetails]
  );

  const handleDeploySuccess = useCallback(
    (deploy: ArtifactDeployment) => {
      onDeploySuccess && onDeploySuccess(deploy);
      getDeployments({ name });
    },
    [getDeployments, name, onDeploySuccess]
  );

  useEffect(() => {
    if (name) {
      getGroups({ name });
      getDeployments({ name });
      getDevices({ name });
    }
  }, [getDeployments, getDevices, getGroups, id, name]);

  useEffect(() => {
    if (groupDetails) {
      onGroupDetailsLoaded && onGroupDetailsLoaded(groupDetails, deviceCount);
    }
  }, [deviceCount, groupDetails, onGroupDetailsLoaded]);

  useEffect(() => {
    const errorMessage =
      groupDetailsStatus === 404
        ? `Sorry, we are unable to retrieve information about the group "${name}""`
        : groupDetailsError || deploymentsError || '';
    if (errorMessage) {
      setToastMessage(errorMessage);
      setToastSeverity('error');

      if (groupDetailsStatus === 404) {
        navigate(groupsViewRoute);
      }
    }
  }, [
    deploymentsError,
    groupDetailsError,
    setToastMessage,
    setToastSeverity,
    groupDetailsStatus,
    name,
    navigate,
    groupsViewRoute,
  ]);

  return (
    <div>
      <Box mb={2} mt={2} component='section'>
        <LegacyDetailsList items={detailItems} />
      </Box>
      <Box mb={2} mt={2} component='section'>
        <Box mb={2}>
          <Divider />
          <Box display='flex' marginTop={2} justifyContent='space-between'>
            <Typography variant='h5'>Deployments</Typography>
            <GroupingToggleButton grouping={deploymentsView} onGroupingChange={handleChangeDeploymentsView} />
          </Box>
        </Box>
        {isDefaultGroup && (
          <Alert severity='info' variant='outlined'>
            {`Deployments are not associated with the '${name}' group`}
          </Alert>
        )}

        {deploymentsError && !isDefaultGroup && (
          <Alert severity='error' variant='outlined'>
            Unable to retreive deployments
          </Alert>
        )}

        {!deploymentsBusy && !deploymentsError && !isDefaultGroup && deploymentsView === 'grouped' ? (
          deployments ? (
            [...DEPLOY_TYPE_MAP].map(([type, text]) => (
              <DeploymentsList
                key={type}
                deployments={deployments}
                projectViewRoute={projectViewRoute}
                deployType={type}
                isBusy={deploymentsBusy}
                isGrouped
                linkConfig
                noRowsMessage={<span>No {text}s have been deployed to this group</span>}
              />
            ))
          ) : (
            <Alert severity='warning' variant='outlined'>
              You have not deployed anything to this group
            </Alert>
          )
        ) : (
          !isDefaultGroup && (
            <DeploymentsList
              deployments={deployments}
              projectViewRoute={projectViewRoute}
              isBusy={deploymentsBusy}
              linkConfig
              noDataMessage={
                <Alert severity='warning' variant='outlined'>
                  You have not deployed anything to this group
                </Alert>
              }
            />
          )
        )}
      </Box>
      {showDeleteGroupModal && (
        <DeleteGroupModal
          id={id}
          open={true}
          onClose={onCloseDeleteGroupModal}
          onSuccess={onDeleteGroupSuccess}
          onError={onDeleteGroupError}
        />
      )}
      {groupDetails && showDeployModal && (
        <DeployToGroupModal
          group={groupDetails}
          deployType={showDeployModal}
          open={true}
          controllerBuildEnabled={controllerBuildEnabled}
          minVersion={minDeployedOsVersion}
          onClose={onCloseDeployModal}
          onSuccess={handleDeploySuccess}
          onCreateNewProject={onCreateNewProject}
        />
      )}
    </div>
  );
}
